"use client";

import { useRouter } from 'next/navigation';
import { Search, Plus } from 'lucide-react';
import boxImg from '@/assets/box.png';
import logoImg from '@/assets/logo.png';
import { useStore, getters } from '@/store';
import Image from 'next/image';
import DownloadAppButton from '@/components/DownloadAppButton';

export default function HomeContent() {
  const router = useRouter();
  const { state } = useStore();
  const isAuthenticated = getters.isAuthenticated(state);
  

  const startAsSeeker = () => {
    router.push('/providers?type=seeker');
  };

  const startAsProvider = () => {
    if (isAuthenticated) {
      router.push('/dashboard');
    } else {
      router.push('/register?type=provider');
    }
  };

  return (
    <div
      className="
        min-h-[100vh]
        flex items-center justify-center
        px-4 py-10
        bg-[linear-gradient(135deg,#eef2ff_0%,#f3e8ff_50%,#fce7f3_100%)]
        antialiased
      "
      role="main"
    >
      <div className="w-full max-w-md flex flex-col items-center text-center">
        
        {/* Title & Logo Section */}
        <div className="mb-8 flex flex-col items-center gap-3">
          
          {/* ✅ FIXED LOGO: Responsive height & auto width */}
          <div className="relative h-16 sm:h-24 w-auto">
            <img 
                src={logoImg.src} 
                alt="CardBox Logo" 
                className="
                h-full
                w-auto             /* Maintain aspect ratio */
                object-contain 
                drop-shadow-sm 
                hover:scale-105 
                transition-transform 
                duration-300
                " 
            />
          </div>

          <p className="m-0 text-base sm:text-lg text-slate-500 font-normal">
            Connect with local service providers
          </p>
        </div>

        {/* Box Image */}
        <div className="mb-6 relative">
          <img
            src={boxImg.src}
            alt="CardBox illustration"
            draggable="false"
            className="
              w-44 h-44 sm:w-72 sm:h-72
              object-contain
              drop-shadow-[0_20px_40px_rgba(99,102,241,0.15)]
              select-none pointer-events-none
              animate-float
            "
          />
        </div>

        {/* Actions */}
        <div className="flex gap-6 sm:gap-10 justify-center w-full max-w-sm">
          {/* Find Cards */}
          <div className="flex flex-col items-center gap-4">
            <button
              aria-label="Find Cards"
              onClick={startAsSeeker}
              className="
                w-16 h-16 sm:w-18 sm:h-18
                rounded-full
                flex items-center justify-center
                bg-gradient-to-br from-indigo-500 to-violet-500
                text-white
                shadow-[0_15px_35px_-10px_rgba(99,102,241,0.5)]
                transition-all duration-300
                ease-[cubic-bezier(0.34,1.56,0.64,1)]
                hover:-translate-y-2 hover:scale-105
                hover:shadow-[0_20px_40px_-10px_rgba(99,102,241,0.7)]
                active:translate-y-1 active:scale-95
              "
            >
              <Search className="w-7 h-7" />
            </button>
            <span className="text-sm sm:text-base font-semibold text-slate-600 transition-colors duration-300">
              Find Cards
            </span>
          </div>

          {/* Add Your Card */}
          <div className="flex flex-col items-center gap-4">
            <button
              aria-label="Add Your Card"
              onClick={startAsProvider}
              className="
                w-16 h-16 sm:w-18 sm:h-18
                rounded-full
                flex items-center justify-center
                bg-white
                text-violet-500
                shadow-[0_15px_35px_-10px_rgba(139,92,246,0.4)]
                transition-all duration-300
                ease-[cubic-bezier(0.34,1.56,0.64,1)]
                hover:-translate-y-2 hover:scale-105
                hover:shadow-[0_20px_40px_-10px_rgba(139,92,246,0.6)]
                active:translate-y-1 active:scale-95
              "
            >
              <Plus className="w-7 h-7" />
            </button>
            <span className="text-sm sm:text-base font-semibold text-slate-600 transition-colors duration-300">
              Add Your Card
            </span>
          </div>

          {/* Renders nothing until an APK has been published. */}
          <DownloadAppButton variant="hero" label="Get the App" />
        </div>
      </div>
    </div>
  );
}
